/**
 * Accounting KPIs of one seat-month (07.1 NIM, docs/models README §3 step 14): everything computable from the closed state and
 * the month's postings. The price KPIs are the step-14 price's (`steps/price.ts`), `camelsComposite` the supervision step's.
 * Definitions: SS `/conventions[id=nim]` for NIM; the deposit ratios below have no chapter definition.
 */
import { cumulativeDepositBeta, DEFAULT_MIN_POLICY_MOVE_RATE } from "./deposit-beta";
import {
  bookBalanceUsd, DEPOSIT_BOOKS, HTM_BOOKS, LOAN_BOOKS, totalAssetsUsd, WHOLESALE_BOOKS,
  type BankState, type BookId, type CapitalSnapshot, type KpiId, type KpiSnapshot, type LiquiditySnapshot, type Posting, type ProductId, type QuarterRecord, type Segment,
} from "@bsim/domain";
import { ONE_OFF_OPEX_PREFIX } from "@bsim/models";
import { niiSensitivityLossRate, tangibleEquityUsd } from "@bsim/scoring";
import { lastKeyLookup } from "./last-key-lookup";

/** The month's P&L, its opening and closing book balances B(m−1), B(m), and closing total assets. */
export interface MonthFlows {
  readonly pnl: PnlSummary;
  readonly openingUsd: (book: BookId) => number;
  readonly closingUsd: (book: BookId) => number;
  readonly totalAssetsUsd: number;
  /** Cumulative IB β's fixed SimStart weights and coupons by product, and FF(m) − FF(0). */
  readonly ibDepositStart: IbDepositStart;
  readonly ffChangeRate: number;
  /** The smallest policy move a beta is read on (`readings.depositBetaMinPolicyMoveRate`); absent = 25bp. */
  readonly minPolicyMoveRate?: number;
  /** Each product's segment, for the CRE concentration KPIs. */
  readonly segmentByProduct: ReadonlyMap<ProductId, Segment>;
}

/** The month's P&L read off the postings by account class (income:/expense: accounts; interest, fees, opex and tax lines). */
export interface PnlSummary {
  readonly niiUsd: number;
  /** The part of niiUsd that is the live swaps' net interest (05.4 E3): reported in NII, outside the core earnings (10.1 E4a). */
  readonly swapInterestUsd: number;
  /** The part of opexUsd that is non-recurring (07.3 H, E6 R₁, E7 lease exit): reported in opex, added back to the core earnings (10.1 E5a). */
  readonly oneOffOpexUsd: number;
  /** The part of the fees that is the cost of a card override lapsing at a known month (OR-30.38): negative fee income, counted once, reported inside feesUsd; positive = cost. */
  readonly lapsingOverrideUsd: number;
  readonly feesUsd: number;
  readonly opexUsd: number;
  readonly cdiAmortizationUsd: number;
  readonly taxUsd: number;
  readonly netChargeOffsUsd: number;
  readonly provisionUsd: number;
  /** The month's dividends (driver `dividend`), for the per-share KPIs. */
  readonly dividendsUsd: number;
  readonly pretaxIncomeUsd: number;
  readonly netIncomeUsd: number;
  /** Interest expense by deposit book (07.1 E2), for the deposit-cost KPIs. */
  readonly depositInterestUsd: Readonly<Partial<Record<BookId, number>>>;
}

const INTEREST_INCOME = "income:interest.";
const INTEREST_EXPENSE = "expense:interest.";
const SWAP_INTEREST_INCOME = "income:interest.swap";
const FEE_INCOME = "income:fee.";
const OPEX = "expense:opex.";
export const TAX_EXPENSE = "expense:tax";
const CDI_OPEX = "expense:opex.cdi";
const ONE_OFF_OPEX = `expense:opex.${ONE_OFF_OPEX_PREFIX}`;
/** OR-30.38: the fee key (`fee.lapsing.<line>`) a card override that lapses at a known month posts its own part of a fee cost to; its own statement line, inside the fee total. */
export const LAPSING_FEE_PREFIX = "lapsing.";
const LAPSING_FEE = `income:fee.${LAPSING_FEE_PREFIX}`;

/** Signed contribution of a posting to an income (credit +) or expense (debit +) account. */
export function summarizePostings(postings: readonly Posting[]): PnlSummary {
  let interestIncome = 0, swapInterest = 0, interestExpense = 0, fees = 0, opex = 0, oneOff = 0, lapsing = 0, cdi = 0, tax = 0, nco = 0, provision = 0, dividends = 0, income = 0, expense = 0;
  const depositInterestUsd: Partial<Record<BookId, number>> = {};
  for (const p of postings) {
    const { debit: dr, credit: cr, amountUsd: a } = p;
    if (cr.startsWith("income:")) { income += a; if (cr.startsWith(INTEREST_INCOME)) { interestIncome += a; if (cr === SWAP_INTEREST_INCOME) swapInterest += a; } else if (cr.startsWith(FEE_INCOME)) { fees += a; if (cr.startsWith(LAPSING_FEE)) lapsing -= a; } }
    if (dr.startsWith("income:")) { income -= a; if (dr.startsWith(INTEREST_INCOME)) { interestIncome -= a; if (dr === SWAP_INTEREST_INCOME) swapInterest -= a; } else if (dr.startsWith(FEE_INCOME)) { fees -= a; if (dr.startsWith(LAPSING_FEE)) lapsing += a; } }
    if (dr.startsWith("expense:")) {
      expense += a;
      if (dr.startsWith(INTEREST_EXPENSE)) { interestExpense += a; const book = dr.slice(INTEREST_EXPENSE.length) as BookId; depositInterestUsd[book] = (depositInterestUsd[book] ?? 0) + a; }
      else if (dr.startsWith(OPEX)) { opex += a; if (dr === CDI_OPEX) cdi += a; else if (dr.startsWith(ONE_OFF_OPEX)) oneOff += a; }
      else if (dr === TAX_EXPENSE) tax += a;
    }
    if (cr.startsWith("expense:")) {
      expense -= a;
      if (cr.startsWith(INTEREST_EXPENSE)) { interestExpense -= a; const book = cr.slice(INTEREST_EXPENSE.length) as BookId; depositInterestUsd[book] = (depositInterestUsd[book] ?? 0) - a; }
      else if (cr.startsWith(OPEX)) { opex -= a; if (cr === CDI_OPEX) cdi -= a; else if (cr.startsWith(ONE_OFF_OPEX)) oneOff -= a; }
      else if (cr === TAX_EXPENSE) tax -= a;
    }
    if (p.driver === "chargeOff") nco += a; else if (p.driver === "recovery") nco -= a;
    else if (p.driver === "provision") provision += dr.startsWith("expense:") ? a : -a;
    else if (p.driver === "dividend") dividends += a;
  }
  return {
    niiUsd: interestIncome - interestExpense, swapInterestUsd: swapInterest, oneOffOpexUsd: oneOff, lapsingOverrideUsd: lapsing, feesUsd: fees, opexUsd: opex, cdiAmortizationUsd: cdi, taxUsd: tax, netChargeOffsUsd: nco, provisionUsd: provision, dividendsUsd: dividends,
    pretaxIncomeUsd: income - expense + tax, netIncomeUsd: income - expense, depositInterestUsd,
  };
}

/** The I17 concentration numerators in one pass over the CRE book: regulatory CRE (owner-occupied excluded) and construction. */
export function creConcentrationUsd(bank: BankState, segmentByProduct: MonthFlows["segmentByProduct"]): { readonly regulatoryUsd: number; readonly constructionUsd: number } {
  let regulatoryUsd = 0, constructionUsd = 0;
  const segmentOf = lastKeyLookup(segmentByProduct);
  for (const v of bank.books.cre.vintages) {
    const segment = segmentOf(v.productId);
    if (segment === "cre_ownerOccupied") continue;
    regulatoryUsd += v.balanceUsd;
    if (segment === "cre_construction") constructionUsd += v.balanceUsd;
  }
  return { regulatoryUsd, constructionUsd };
}

const sumOf = (books: readonly BookId[], usd: (book: BookId) => number): number => {
  let t = 0;
  for (const b of books) t += usd(b);
  return t;
};
export const EARNING_BOOKS = ["cash", "ust_afs", "mbs_afs", "mbs_htm", ...LOAN_BOOKS] as const satisfies readonly BookId[];
/** The interest-bearing deposit books (brokered included, as cumulative IB β weighs them). */
export const IB_DEPOSIT_BOOKS: readonly BookId[] = DEPOSIT_BOOKS.filter((b) => b !== "dda");
/** Each interest-bearing deposit product's SimStart balance and balance-weighted coupon: the fixed weights of cumulative IB β. */
export type IbDepositStart = ReadonlyMap<ProductId, { readonly balanceUsd: number; readonly couponRate: number }>;

/**
 * Cumulative IB β (01.3; oracle ch01 `cumulative_beta`; one definition with the book lines and the ALM view, `cumulativeDepositBeta`): Σ_p W_p·(c̄_p(m) − c̄_p(0)) ÷ Σ W ÷ (FF(m) − FF(0)) over the interest-bearing
 * deposit products, W_p and c̄_p(0) their SimStart balances and balance-weighted coupons, c̄_p(m) now; absent while FF has moved under 25bp from SimStart.
 */
function cumulativeBeta(bank: BankState, start: MonthFlows["ibDepositStart"], ffChangeRate: number, minMoveRate: number): number | undefined {
  if (Math.abs(ffChangeRate) < minMoveRate) return undefined;
  const now = new Map<ProductId, { usd: number; coupon: number }>();
  for (const b of IB_DEPOSIT_BOOKS) {
    for (const v of bank.books[b].vintages) { const p = now.get(v.productId) ?? { usd: 0, coupon: 0 }; p.usd += v.balanceUsd; p.coupon += v.balanceUsd * v.couponRate; now.set(v.productId, p); }
  }
  let moved = 0, weight = 0;
  for (const [id, s] of start) {
    const p = now.get(id);
    weight += s.balanceUsd;
    if (p !== undefined && p.usd > 0) moved += s.balanceUsd * (p.coupon / p.usd - s.couponRate);
  }
  return weight === 0 ? undefined : cumulativeDepositBeta(moved / weight, ffChangeRate, minMoveRate);
}

/**
 * Annualised growth of `books` since the newest quarter record at least `windowMonths` back (else the oldest earlier one);
 * undefined with no record. Only records carrying `booksUsd` count.
 */
function trailingGrowthRate(bank: BankState, closingUsd: MonthFlows["closingUsd"], books: readonly BookId[], windowMonths: number): number | undefined {
  let ref: QuarterRecord | undefined;
  for (const q of bank.quarters) {
    if (q.booksUsd === undefined || q.quarterEndMonth >= bank.month) continue;
    const inWindow = q.quarterEndMonth <= bank.month - windowMonths;
    const refInWindow = ref !== undefined && ref.quarterEndMonth <= bank.month - windowMonths;
    if (ref === undefined || (inWindow ? !refInWindow || q.quarterEndMonth > ref.quarterEndMonth : !refInWindow && q.quarterEndMonth < ref.quarterEndMonth)) ref = q;
  }
  if (ref?.booksUsd === undefined) return undefined;
  let then = 0;
  for (const b of books) then += ref.booksUsd[b] ?? 0;
  return then === 0 ? undefined : Math.pow(sumOf(books, closingUsd) / then, 12 / (bank.month - ref.quarterEndMonth)) - 1;
}

type Kpis = Partial<Record<KpiId, number>>;
const put = (kpis: Kpis, id: KpiId, value: number | undefined): void => { if (value !== undefined && Number.isFinite(value)) kpis[id] = value; };
const ratio = (num: number, den: number): number | undefined => (den === 0 ? undefined : num / den);

/** The KPIs of a closed balance sheet alone (no month flows): capital, IRR, marks, concentration, funding mix and liquidity. */
export function balanceSheetKpis(bank: BankState, closingUsd: MonthFlows["closingUsd"], totalAssetsUsd: number, segmentByProduct: MonthFlows["segmentByProduct"], capital: CapitalSnapshot, liquidity: LiquiditySnapshot): Kpis {
  const kpis: Kpis = {};
  const deposits = sumOf(DEPOSIT_BOOKS, closingUsd);
  const loans = sumOf(LOAN_BOOKS, closingUsd);
  put(kpis, "ddaShare", ratio(closingUsd("dda"), deposits));
  put(kpis, "ldr", ratio(loans, deposits));
  // Adjusted LDR = gross loans ÷ (deposits − brokered), the wholesale-like deposit taken out of the funding base.
  put(kpis, "adjLdr", ratio(loans, deposits - closingUsd("brokered")));
  put(kpis, "cet1Reported", ratio(capital.cet1Usd, capital.rwaUsd));
  put(kpis, "cet1Marked", ratio(capital.markedCet1Usd, capital.rwaUsd));
  put(kpis, "tier1", ratio(capital.tier1Usd, capital.rwaUsd));
  put(kpis, "trbc", ratio(capital.trbcUsd, capital.rwaUsd));
  put(kpis, "leverage", ratio(capital.tier1Usd, capital.avgAssetsForLeverageUsd));
  put(kpis, "rwa", capital.rwaUsd);
  put(kpis, "aoci", bank.equity.aociAfsUsd + bank.equity.aociSwapUsd);
  put(kpis, "eve200", bank.irr.eve200Rate);
  put(kpis, "niiSensitivity", bank.irr.niiSensitivityRate);
  put(kpis, "niiSensitivityLossRate", niiSensitivityLossRate(bank.irr));
  put(kpis, "htmMark", HTM_BOOKS.reduce((s, b) => s + bank.books[b].vintages.reduce((t, v) => t + (v.fairValueUsd ?? v.balanceUsd) - v.balanceUsd, 0), 0));
  // 08.1 §7 (I17): regulatory CRE = construction + office + other IPRE (owner-occupied excluded) and construction alone, over TRBC in dollars.
  const cre = creConcentrationUsd(bank, segmentByProduct);
  put(kpis, "creToTrbc", ratio(cre.regulatoryUsd, capital.trbcUsd));
  put(kpis, "constructionToTrbc", ratio(cre.constructionUsd, capital.trbcUsd));
  put(kpis, "wholesaleShare", ratio(sumOf(WHOLESALE_BOOKS, closingUsd), totalAssetsUsd));
  if (liquidity.netOutflow30dUsd > 0) {
    put(kpis, "uninsuredShare", ratio(liquidity.uninsuredDepositsUsd, deposits));
    put(kpis, "lcrLite", liquidity.lcrLiteRate);
    put(kpis, "slc", liquidity.slcRate);
  }
  return kpis;
}

/**
 * The accounting KpiSnapshot for `bank` (the closed month-end state). NIM uses the mean of the opening and closing balances
 * (simstart_v3 conventions.nim), annualised (12 × the month's flow). The deposit costs are the month's interest over the opening
 * balances it accrued on (07.1 §5: intra-month flows earn nothing), so they are the balance-weighted rate paid, as at SimStart
 * (`simStartKpis`) and in 01.3's cost measures: a month's inflow does not dilute its own rate. Liquidity KPIs are present only once a
 * real snapshot exists.
 */
export function accountingKpis(bank: BankState, month: MonthFlows, capital: CapitalSnapshot, liquidity: LiquiditySnapshot): KpiSnapshot {
  const { pnl, openingUsd, closingUsd, segmentByProduct } = month;
  const meanOf = (books: readonly BookId[]): number => (sumOf(books, openingUsd) + sumOf(books, closingUsd)) / 2;
  let ibInterest = 0, allInterest = 0;
  for (const b of DEPOSIT_BOOKS) { const i = pnl.depositInterestUsd[b] ?? 0; allInterest += i; if (b !== "dda") ibInterest += i; }
  const kpis: Kpis = {};
  put(kpis, "nii", pnl.niiUsd);
  put(kpis, "nim", ratio(12 * pnl.niiUsd, meanOf(EARNING_BOOKS)));
  put(kpis, "costOfDeposits", ratio(12 * allInterest, sumOf(DEPOSIT_BOOKS, openingUsd)));
  put(kpis, "ibDepositCost", ratio(12 * ibInterest, sumOf(IB_DEPOSIT_BOOKS, openingUsd)));
  put(kpis, "loanGrowth", trailingGrowthRate(bank, closingUsd, LOAN_BOOKS, 12));
  put(kpis, "depositGrowth", trailingGrowthRate(bank, closingUsd, DEPOSIT_BOOKS, 12));
  put(kpis, "cumulativeBeta", cumulativeBeta(bank, month.ibDepositStart, month.ffChangeRate, month.minPolicyMoveRate ?? DEFAULT_MIN_POLICY_MOVE_RATE));
  // 04.2, 04.3: the month's annualised NCO over mean loans, nonaccrual and ACL over closing loans, the month's provision.
  const loans = sumOf(LOAN_BOOKS, closingUsd);
  put(kpis, "ncoRate", ratio(12 * pnl.netChargeOffsUsd, meanOf(LOAN_BOOKS)));
  put(kpis, "nplRate", ratio(LOAN_BOOKS.reduce((t, b) => t + (bank.credit?.[b]?.nonaccrualUsd ?? 0), 0), loans));
  put(kpis, "aclRatio", ratio(bank.aclUsd, loans));
  put(kpis, "provision", pnl.provisionUsd);
  // Monthly returns KPIs: rates are the month's flow annualised over closing total assets and
  // tangible equity (as `nim` is); efficiency is the month's opex (CDI included, as PPNR is) over NII + fees; EPS and DPS are the month's
  // per-share flows, so a year's months sum to the year's EPS and DPS (the G7 rows sum DPS over a year); fees annualise like NII.
  put(kpis, "roa", ratio(12 * pnl.netIncomeUsd, month.totalAssetsUsd));
  put(kpis, "rotce", ratio(12 * pnl.netIncomeUsd, tangibleEquityUsd(bank)));
  put(kpis, "eps", ratio(pnl.netIncomeUsd, bank.sharesOutstanding));
  put(kpis, "dps", pnl.dividendsUsd > 0 ? bank.dpsLastUsdPerShare : 0);
  put(kpis, "efficiencyRatio", ratio(pnl.opexUsd, pnl.niiUsd + pnl.feesUsd));
  put(kpis, "feeIncome", 12 * pnl.feesUsd);
  return { ...kpis, ...balanceSheetKpis(bank, closingUsd, month.totalAssetsUsd, segmentByProduct, capital, liquidity) };
}

/**
 * Every KPI a SimStart bank has a value for (the pack's own KPIs win where both exist): the balance-sheet KPIs above; flow KPIs on
 * the trailing year of the four quarter records (NI, dividends, fees, PPNR, NCO) and the pack's trailing-year NII (opex = NII + fees
 * − PPNR): ROA, ROTCE, EPS, DPS, efficiency, fee income, NCO rate; NPL and ACL ratios on the SimStart credit state; the deposit costs
 * as the run-rate of the SimStart coupons (07.1 E2 on B(0)); P/TBV from the pack's price and TBV per share; CRE growth over 36
 * months, 0 at m = 0 (03.2 §7). Growth rates with no year-earlier record, β since SimStart, ROE and Value Created stay absent.
 */
export function simStartKpis(bank: BankState, segmentByProduct: MonthFlows["segmentByProduct"]): KpiSnapshot {
  const kpis: Kpis = seededKpis(bank, segmentByProduct);
  return { ...kpis, ...bank.kpis };
}

/**
 * The SimStart KPI no monthly step recomputes (03.2's CRE growth has no history to read). It describes SimStart only, so a run drops it
 * from the state it opens rather than carry month 0's value forward as if it were current (an object rest, not a `delete`, so the
 * run's KPI record keeps a fast shape).
 */
export function withoutSimStartOnly(kpis: KpiSnapshot): KpiSnapshot {
  const { creGrowth36m: _simStartOnly, ...current } = kpis;
  return current;
}

function seededKpis(bank: BankState, segmentByProduct: MonthFlows["segmentByProduct"]): Kpis {
  const closingUsd = (b: BookId): number => bookBalanceUsd(bank, b);
  const couponUsd = (b: BookId): number => bank.books[b].vintages.reduce((t, v) => t + v.balanceUsd * v.couponRate, 0);
  const ta = totalAssetsUsd(bank);
  const kpis = balanceSheetKpis(bank, closingUsd, ta, segmentByProduct, bank.capital, bank.liquidity);
  let ni = 0, dividends = 0, fees = 0, ppnr = 0, nco = 0;
  for (const q of bank.quarters.slice(-4)) { ni += q.netIncomeUsd; dividends += q.dividendsUsd; fees += q.feesUsd ?? 0; ppnr += q.ppnrUsd ?? 0; nco += q.ncoUsd ?? 0; }
  const revenue = (bank.kpis.nii ?? 0) + fees, loans = sumOf(LOAN_BOOKS, closingUsd);
  let nonaccrual = 0;
  for (const b of LOAN_BOOKS) nonaccrual += bank.credit?.[b]?.nonaccrualUsd ?? 0;
  put(kpis, "roa", ratio(ni, ta));
  put(kpis, "rotce", ratio(ni, tangibleEquityUsd(bank)));
  put(kpis, "eps", ratio(ni, bank.sharesOutstanding));
  put(kpis, "dps", ratio(dividends, bank.sharesOutstanding));
  put(kpis, "efficiencyRatio", ratio(revenue - ppnr, revenue));
  put(kpis, "feeIncome", fees);
  put(kpis, "ncoRate", ratio(nco, loans));
  put(kpis, "nplRate", ratio(nonaccrual, loans));
  put(kpis, "aclRatio", ratio(bank.aclUsd, loans));
  put(kpis, "costOfDeposits", ratio(sumOf(DEPOSIT_BOOKS, couponUsd), sumOf(DEPOSIT_BOOKS, closingUsd)));
  put(kpis, "ibDepositCost", ratio(sumOf(IB_DEPOSIT_BOOKS, couponUsd), sumOf(IB_DEPOSIT_BOOKS, closingUsd)));
  const { price, tbvps } = bank.kpis;
  if (price !== undefined && tbvps !== undefined) put(kpis, "ptbv", ratio(price, tbvps));
  put(kpis, "creGrowth36m", 0);
  return kpis;
}
